import {
    Checkbox,
    Grid,
    Stack,
    Typography,
    useTheme,
    CircularProgress,
    Tooltip,
    Select,
    MenuItem,
    ListItemText,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import CustomTextField from '../../../components/common/CustomTextField'
import { EventInPlanningDto } from '../../../services/api/sharedTypes/Event'
import { ProductWithStatisticsDto } from '../../../services/api/sharedTypes/Product'
import { ContractorBriefDto, ContractorType } from '../../../services/api/sharedTypes/Contractor'
import EventDetailDialog from '../detail/EventDetailDialog'
import PlusButton from '../../../components/event/PlusButton'
import { useDebounce } from '../../../utils/useDebounce'
import {
    convertContractorBriefDtoToContractorOfEventDto,
    findNameOfContractorAssignedToDifferentEventById,
} from '../../../utils/contractor-utils'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { convertProductWithStatisticsDtoToProductOfEventDto } from '../../../utils/product-utils'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'

interface EventDetailForPlanningProps {
    event: EventInPlanningDto
    onRefetch: () => void
    onEventChange: (event: EventInPlanningDto) => void
    productList: ProductWithStatisticsDto[] | undefined
    contractorList: ContractorBriefDto[] | undefined
    loadingProducts: boolean
    loadingContractors: boolean
    userIsAdmin: boolean
}

const EventDetailForPlanning: React.FC<EventDetailForPlanningProps> = ({
    event,
    onRefetch,
    onEventChange,
    productList,
    contractorList,
    loadingProducts,
    loadingContractors,
    userIsAdmin,
}: EventDetailForPlanningProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [localEvent, setLocalEvent] = useState<EventInPlanningDto>(event)

    const [debouncedNoteValue, noteValue, setNoteValue] = useDebounce<string>(event.planningNote, 1000)

    const contractorsInDifferentEvent = useMemo(
        () =>
            localEvent.contractors
                .map((contractor) => {
                    return contractor.contractorId
                        ? findNameOfContractorAssignedToDifferentEventById(
                            contractor.contractorId,
                            localEvent.id,
                            localEvent.startsAt,
                            localEvent.endsAt,
                            contractorList ?? []
                        )
                        : null
                })
                .filter((contractor) => contractor !== null),
        [localEvent, contractorList]
    )

    const isThereEmptySelect =
        localEvent.products.some((product) => product.productId === '') ||
        localEvent.contractors.some((contractor) => contractor.contractorId === '')

    useEffect(() => {
        if (debouncedNoteValue === event.planningNote) return
        handleChange({ ...localEvent, planningNote: debouncedNoteValue })
    }, [debouncedNoteValue])

    const handleChange = (newEvent: EventInPlanningDto) => {
        if (newEvent.products.some((product) => product.productId === ''))
            return dispatch(
                displayMessage({
                    message: 'Nejdříve zvolte prázndou službu',
                    type: 'warning',
                })
            )
        setLocalEvent(newEvent)
        onEventChange(newEvent)
    }

    if (!localEvent) return <Typography>Událost nenalezena</Typography>

    return (
        <Stack
            border='1px solid'
            borderColor={theme.palette.tableHeader.default}
            borderRadius='5px'
            overflow='auto'
            bgcolor={theme.palette.customBackground.default}
        >
            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                bgcolor={localEvent.isEverythingReady ? theme.palette.success.main : theme.palette.tableHeader.default}
                width='100%'
                paddingLeft={2}
                style={{ borderTopLeftRadius: '5px', borderTopRightRadius: '5px' }}
                spacing={2}
            >
                <Stack
                    direction='row'
                    width='100%'
                    alignContent='center'
                    gap={1}
                    overflow='hidden'
                    justifyContent='space-between'
                >
                    <Tooltip
                        style={{ fontWeight: 'bold' }}
                        title={
                            new Date(localEvent.startsAt).toLocaleString('cs', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            }) +
                            ' - ' +
                            new Date(localEvent.endsAt).toLocaleString('cs', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                        }
                    >
                        <Typography style={{ fontWeight: 'bold' }} variant='body1' minWidth='fit-content'>
                            {new Date(localEvent.startsAt).toLocaleString('cs', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}{' '}
                            -{' '}
                            {new Date(localEvent.endsAt).toLocaleString('cs', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </Typography>
                    </Tooltip>
                    <Tooltip
                        title={`${localEvent.address?.street ?? ''} ${localEvent.address?.zip ?? ''} ${localEvent.address?.city ?? ''
                            }`}
                    >
                        <Typography style={{ fontWeight: 'bold' }} variant='body1' noWrap minWidth='100px' mx={3}>
                            {`${localEvent.address?.street ?? ''} ${localEvent.address?.zip ?? ''} ${localEvent.address?.city ?? ''
                                }`}
                        </Typography>
                    </Tooltip>
                    <Tooltip title={localEvent.contacts?.map((contact) => contact.phone).join(', ')}>
                        {localEvent.contacts.length > 0 ? (
                            <Typography style={{ fontWeight: 'bold' }} variant='body1' minWidth='fit-content'>
                                {localEvent.contacts.length === 1
                                    ? localEvent.contacts[0].phone
                                    : localEvent.contacts[0].phone +
                                    ' (+' +
                                    (Number(localEvent.contacts.length) - 1).toString() +
                                    ')'}
                            </Typography>
                        ) : (
                            <Typography color='transparent'>Placeholder</Typography>
                        )}
                    </Tooltip>
                </Stack>

                <Stack direction='row' alignContent='center' p={2}>
                    <Checkbox
                        checked={localEvent.isEverythingReady}
                        style={{ padding: 0 }}
                        onClick={() => {
                            if (isThereEmptySelect)
                                return dispatch(
                                    displayMessage({
                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                        type: 'warning',
                                    })
                                )
                            handleChange({ ...localEvent, isEverythingReady: !localEvent.isEverythingReady })
                        }}
                    />
                    {
                        userIsAdmin && (
                            <EventDetailDialog onRefetch={onRefetch} eventId={localEvent.id} />
                        )
                    }
                </Stack>
            </Stack>

            <Stack direction='row' justifyContent='space-between'>
                <Grid container rowSpacing={1} columnSpacing={4} padding={2} width='50%'>
                    <Grid item xs={6.1}>
                        <Stack gap={1}>
                            <Stack direction='row' justifyContent='space-between'>
                                <Stack direction='row' spacing={1}>
                                    <Typography style={{ fontWeight: 'bold' }}>Služba</Typography>
                                    <Typography>({localEvent.products.length})</Typography>
                                </Stack>
                                <PlusButton
                                    disabled={!userIsAdmin}
                                    onClick={() => {
                                        if (!userIsAdmin)
                                            return dispatch(
                                                displayMessage({
                                                    message: 'Nemáte oprávnění pro přidání služby',
                                                    type: 'error',
                                                })
                                            )
                                        if (isThereEmptySelect)
                                            return dispatch(
                                                displayMessage({
                                                    message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                    type: 'warning',
                                                })
                                            )
                                        setLocalEvent({
                                            ...localEvent,
                                            products: [
                                                ...localEvent.products,
                                                {
                                                    productId: '',
                                                    costs: 0,
                                                    isConfirmed: false,
                                                    operatingTime: '',
                                                    quantity: 1,
                                                    productName: '',
                                                    revenue: 0,
                                                },
                                            ],
                                        })
                                    }}
                                />
                            </Stack>

                            {loadingProducts ? (
                                <CircularProgress />
                            ) : productList ? (
                                localEvent.products.length > 0 ? (
                                    localEvent.products.map((product, index) => (
                                        <Stack direction='row' gap={1} key={product.productId}>

                                            <Select
                                                value={product.productId}
                                                size='small'
                                                style={{
                                                    maxHeight: '33px',
                                                }}
                                                placeholder='Služba'
                                                fullWidth
                                                disabled={!userIsAdmin}
                                                onChange={(e) => {
                                                    if (!userIsAdmin)
                                                        return dispatch(
                                                            displayMessage({
                                                                message: 'Nemáte oprávnění pro úpravu služby',
                                                                type: 'error',
                                                            })
                                                        )

                                                    const newProduct = productList.find((p) => p.id === e.target.value)
                                                    if (!newProduct) return

                                                    // check if product is already in list
                                                    if (
                                                        localEvent.products.some(
                                                            (p) => p.productId === newProduct.id && p.productId !== product.productId
                                                        )
                                                    )
                                                        return dispatch(
                                                            displayMessage({
                                                                message: 'Služba je již přiřazena',
                                                                type: 'warning',
                                                            })
                                                        )

                                                    handleChange({
                                                        ...localEvent,
                                                        products: localEvent.products.map((localProduct) => {
                                                            if (localProduct.productId === product.productId)
                                                                return convertProductWithStatisticsDtoToProductOfEventDto(newProduct)
                                                            return localProduct
                                                        }),
                                                    })
                                                }}
                                                renderValue={(value) => {
                                                    return product.productName === 'Null' ? '' : product.productName
                                                }}
                                            >
                                                <MenuItem>
                                                    <ListItemText
                                                        primary='Odstranit řádek služby'
                                                        style={{ color: theme.palette.error.main }}
                                                        onClick={() => {
                                                            if (isThereEmptySelect)
                                                                return dispatch(
                                                                    displayMessage({
                                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                        type: 'warning',
                                                                    })
                                                                )
                                                            handleChange({
                                                                ...localEvent,
                                                                products: localEvent.products.filter(
                                                                    (localProduct) => localProduct.productId !== product.productId
                                                                ),
                                                            })
                                                        }}
                                                    />
                                                </MenuItem>
                                                {productList
                                                    .filter((localProduct) => localProduct.canBeAssigned)
                                                    .map((localProduct) => {
                                                        const productForPlanning =
                                                            convertProductWithStatisticsDtoToProductOfEventDto(localProduct)
                                                        return (
                                                            <MenuItem
                                                                key={productForPlanning.productId}
                                                                value={productForPlanning.productId}
                                                            >
                                                                {productForPlanning.productName}
                                                            </MenuItem>
                                                        )
                                                    })}
                                            </Select>
                                            <Checkbox
                                                disabled={product.productId === '' || !userIsAdmin}
                                                checked={product.isConfirmed}
                                                style={{ padding: 0, paddingRight: 1 }}
                                                onChange={(e) => {
                                                    if (!userIsAdmin)
                                                        return dispatch(
                                                            displayMessage({
                                                                message: 'Nemáte oprávnění pro úpravu služby',
                                                                type: 'error',
                                                            })
                                                        )
                                                    if (isThereEmptySelect)
                                                        return dispatch(
                                                            displayMessage({
                                                                message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                type: 'warning',
                                                            })
                                                        )

                                                    handleChange({
                                                        ...localEvent,
                                                        products: localEvent.products.map((localProduct) =>
                                                            product.productId ===
                                                                localProduct.productId &&
                                                                localEvent.products
                                                                    .indexOf(localProduct) === index
                                                                ? {
                                                                    ...product,
                                                                    isConfirmed: e.target.checked,
                                                                }
                                                                : localProduct
                                                        ),
                                                    })
                                                }}
                                            />
                                        </Stack>
                                    ))
                                ) : (
                                    <Typography variant='body1'>Není přiřazena služba</Typography>
                                )
                            ) : (
                                <Typography variant='body1'>Seznam služeb nenačten</Typography>
                            )}
                        </Stack>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container columnSpacing={3} rowSpacing={1}>
                            <Grid item xs={6}>
                                <Stack direction='row' justifyContent='space-between' mr={1}>
                                    <Stack direction='row' spacing={1}>
                                        <Typography style={{ fontWeight: 'bold' }}>Brigádník</Typography>
                                        <Typography>
                                            (
                                            {
                                                localEvent.contractors.filter(
                                                    (contractor) => contractor.contractorType !== ContractorType.Carrier
                                                ).length
                                            }
                                            )
                                        </Typography>
                                    </Stack>
                                    <PlusButton
                                        onClick={() => {
                                            if (isThereEmptySelect)
                                                return dispatch(
                                                    displayMessage({
                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                        type: 'warning',
                                                    })
                                                )
                                            handleChange({
                                                ...localEvent,
                                                contractors: [
                                                    ...localEvent.contractors,
                                                    {
                                                        contractorName: '',
                                                        contractorType: ContractorType.Brigadier,
                                                        costs: 0,
                                                        gotPaid: false,
                                                        hasResponded: false,
                                                        isConfirmed: false,
                                                        isLeader: false,
                                                    },
                                                ],
                                            })
                                        }}
                                    />
                                </Stack>
                            </Grid>
                            <Grid style={{ fontWeight: 'bold' }} item xs={4}>
                                Výplata
                            </Grid>
                            <Grid style={{ fontWeight: 'bold' }} item xs={2}>
                                Zaplaceno
                            </Grid>

                            {loadingContractors ? (
                                <CircularProgress />
                            ) : contractorList ? (
                                localEvent.contractors.filter(
                                    (contractor) => contractor.contractorType !== ContractorType.Carrier
                                ).length > 0 ? (
                                    localEvent.contractors
                                        .filter((contractor) => contractor.contractorType !== ContractorType.Carrier)
                                        .map((contractor, index) => (
                                            <React.Fragment key={index}>
                                                <Grid item xs={6}>
                                                    <Stack direction='row' gap={1}>
                                                        <Select
                                                            value={
                                                                contractor.contractorId !== ''
                                                                    ? contractor.contractorId
                                                                    : undefined
                                                            }
                                                            size='small'
                                                            style={{
                                                                backgroundColor: contractor.isConfirmed
                                                                    ? theme.palette.tableRow?.background.paid
                                                                    : theme.palette.customBackground.default,
                                                                maxHeight: '33px',
                                                                maxWidth: '86%',
                                                                flexGrow: 1,
                                                            }}
                                                            placeholder='Brigádník'
                                                            fullWidth
                                                            onChange={(e) => {
                                                                e.preventDefault()

                                                                if (isThereEmptySelect)
                                                                    return dispatch(
                                                                        displayMessage({
                                                                            message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                            type: 'warning',
                                                                        })
                                                                    )

                                                                const newContractor = contractorList.find(
                                                                    (contractor) => contractor.id === e.target.value
                                                                )

                                                                if (!newContractor) return

                                                                // check if contractor is already in list
                                                                const isContractorInList = localEvent.contractors.some(
                                                                    (localContractor) =>
                                                                        localContractor.contractorId === newContractor.id
                                                                )

                                                                if (isContractorInList)
                                                                    return dispatch(
                                                                        displayMessage({
                                                                            message:
                                                                                'Brigádník je již v přiřazen k této události',
                                                                            type: 'warning',
                                                                        })
                                                                    )

                                                                handleChange({
                                                                    ...localEvent,
                                                                    contractors: localEvent.contractors.map((localContractor) => {
                                                                        if (
                                                                            localEvent.contractors
                                                                                .filter(
                                                                                    (contractor) =>
                                                                                        contractor.contractorType !==
                                                                                        ContractorType.Carrier
                                                                                )
                                                                                .indexOf(localContractor) === index
                                                                        )
                                                                            return {
                                                                                contractorId: newContractor.id,
                                                                                contractorName: newContractor.name,
                                                                                contractorType: newContractor.type,
                                                                                isConfirmed: contractor.isConfirmed,
                                                                                costs: contractor.costs,
                                                                                gotPaid: contractor.gotPaid,
                                                                                hasResponded: contractor.hasResponded,
                                                                                isLeader: contractor.isLeader,
                                                                            }

                                                                        return localContractor
                                                                    }),
                                                                })
                                                            }}
                                                            renderValue={(value) => {
                                                                return contractor.contractorName === 'Null'
                                                                    ? ''
                                                                    : contractor.contractorName
                                                            }}
                                                        >
                                                            <MenuItem>
                                                                <ListItemText
                                                                    primary='Odstranit řádek brigádníka'
                                                                    style={{ color: theme.palette.error.main }}
                                                                    onClick={() => {
                                                                        handleChange({
                                                                            ...localEvent,
                                                                            contractors: localEvent.contractors.filter(
                                                                                (localContractor) =>
                                                                                    localEvent.contractors
                                                                                        .filter(
                                                                                            (contractor) =>
                                                                                                contractor.contractorType !==
                                                                                                ContractorType.Carrier
                                                                                        )
                                                                                        .indexOf(localContractor) !== index
                                                                            ),
                                                                        })
                                                                    }}
                                                                />
                                                            </MenuItem>
                                                            {contractorList
                                                                .filter(
                                                                    (contractor) => contractor.type === ContractorType.Brigadier
                                                                )
                                                                .filter((contractor) => contractor.canBeAssigned)
                                                                .map((localContractor) => {
                                                                    const contractorForPlanning =
                                                                        convertContractorBriefDtoToContractorOfEventDto(
                                                                            localContractor
                                                                        )

                                                                    if (!contractorForPlanning.contractorId) return null

                                                                    return (
                                                                        <MenuItem
                                                                            key={contractorForPlanning.contractorId}
                                                                            value={contractorForPlanning.contractorId}
                                                                        >
                                                                            {contractorForPlanning.contractorName}
                                                                        </MenuItem>
                                                                    )
                                                                })}
                                                        </Select>
                                                        <Checkbox
                                                            disabled={contractor.contractorId === ''}
                                                            checked={contractor.isConfirmed}
                                                            style={{ padding: 0, paddingRight: 1 }}
                                                            onChange={(e) => {
                                                                if (isThereEmptySelect)
                                                                    return dispatch(
                                                                        displayMessage({
                                                                            message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                            type: 'warning',
                                                                        })
                                                                    )

                                                                handleChange({
                                                                    ...localEvent,
                                                                    contractors: localEvent.contractors.map((localContractor) =>
                                                                        contractor.contractorId ===
                                                                            localContractor.contractorId &&
                                                                            localEvent.contractors
                                                                                .filter(
                                                                                    (contractor) =>
                                                                                        contractor.contractorType !==
                                                                                        ContractorType.Carrier
                                                                                )
                                                                                .indexOf(localContractor) === index
                                                                            ? {
                                                                                ...contractor,
                                                                                isConfirmed: e.target.checked,
                                                                            }
                                                                            : localContractor
                                                                    ),
                                                                })
                                                            }}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <CustomTextField
                                                        disabled={contractor.contractorId === ''}
                                                        fullWidth
                                                        isSmall
                                                        size='small'
                                                        placeholder='Výplata'
                                                        value={contractor.costs}
                                                        style={{
                                                            backgroundColor: contractor.gotPaid
                                                                ? theme.palette.tableRow?.background.paid
                                                                : theme.palette.customBackground.default,
                                                        }}
                                                        onChange={(e) => {
                                                            if (isThereEmptySelect)
                                                                return dispatch(
                                                                    displayMessage({
                                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                        type: 'warning',
                                                                    })
                                                                )
                                                            if (isNaN(Number(e.target.value))) return
                                                            setLocalEvent({
                                                                ...localEvent,
                                                                contractors: localEvent.contractors.map((localContractor) =>
                                                                    contractor.contractorId === localContractor.contractorId &&
                                                                        localEvent.contractors
                                                                            .filter(
                                                                                (contractor) =>
                                                                                    contractor.contractorType !==
                                                                                    ContractorType.Carrier
                                                                            )
                                                                            .indexOf(localContractor) === index
                                                                        ? {
                                                                            ...contractor,
                                                                            costs: Number(e.target.value) ?? 0,
                                                                        }
                                                                        : localContractor
                                                                ),
                                                            })
                                                        }}
                                                        onBlur={() => handleChange(localEvent)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleChange(localEvent)
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Checkbox
                                                        disabled={contractor.contractorId === ''}
                                                        checked={contractor.gotPaid}
                                                        style={{ padding: 0, marginTop: 3 }}
                                                        onChange={(e) => {
                                                            if (isThereEmptySelect)
                                                                return dispatch(
                                                                    displayMessage({
                                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                        type: 'warning',
                                                                    })
                                                                )
                                                            handleChange({
                                                                ...localEvent,
                                                                contractors: localEvent.contractors.map((localContractor) =>
                                                                    contractor.contractorId === localContractor.contractorId &&
                                                                        localEvent.contractors
                                                                            .filter(
                                                                                (contractor) =>
                                                                                    contractor.contractorType !==
                                                                                    ContractorType.Carrier
                                                                            )
                                                                            .indexOf(localContractor) === index
                                                                        ? {
                                                                            ...contractor,
                                                                            gotPaid: e.target.checked,
                                                                        }
                                                                        : localContractor
                                                                ),
                                                            })
                                                        }}
                                                    />
                                                </Grid>
                                            </React.Fragment>
                                        ))
                                ) : (
                                    <Typography ml={3} variant='body1'>
                                        Není přiřazen brigádník
                                    </Typography>
                                )
                            ) : (
                                <Typography ml={3} variant='body1'>
                                    Seznam brigádníků nenačten
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container columnSpacing={3} rowSpacing={1}>
                            <Grid item xs={6}>
                                <Stack direction='row' justifyContent='space-between' mr={1}>
                                    <Stack direction='row' spacing={1}>
                                        <Typography style={{ fontWeight: 'bold' }}>Dopravce</Typography>
                                        <Typography>
                                            (
                                            {
                                                localEvent.contractors.filter(
                                                    (contractor) => contractor.contractorType === ContractorType.Carrier
                                                ).length
                                            }
                                            )
                                        </Typography>
                                    </Stack>
                                    <PlusButton
                                        onClick={() => {
                                            if (isThereEmptySelect)
                                                return dispatch(
                                                    displayMessage({
                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                        type: 'warning',
                                                    })
                                                )
                                            setLocalEvent({
                                                ...localEvent,
                                                contractors: [
                                                    ...localEvent.contractors,
                                                    {
                                                        contractorId: '',
                                                        contractorName: '',
                                                        contractorType: ContractorType.Carrier,
                                                        costs: 0,
                                                        gotPaid: false,
                                                        hasResponded: false,
                                                        isConfirmed: false,
                                                        isLeader: false,
                                                    },
                                                ],
                                            })
                                        }}
                                    />
                                </Stack>
                            </Grid>
                            <Grid style={{ fontWeight: 'bold' }} item xs={4}>
                                Výplata
                            </Grid>
                            <Grid style={{ fontWeight: 'bold' }} item xs={2}>
                                Zaplaceno
                            </Grid>

                            {loadingContractors ? (
                                <CircularProgress />
                            ) : contractorList ? (
                                localEvent.contractors.filter(
                                    (contractor) => contractor.contractorType === ContractorType.Carrier
                                ).length > 0 ? (
                                    localEvent.contractors
                                        .filter((contractor) => contractor.contractorType === ContractorType.Carrier)
                                        .map((contractor) => (
                                            <React.Fragment key={contractor.contractorId}>
                                                <Grid item xs={6}>
                                                    <Stack direction='row' gap={1}>
                                                        <Select
                                                            value={
                                                                contractor.contractorId !== ''
                                                                    ? contractor.contractorId
                                                                    : undefined
                                                            }
                                                            size='small'
                                                            style={{
                                                                backgroundColor: contractor.isConfirmed
                                                                    ? theme.palette.tableRow?.background.paid
                                                                    : theme.palette.customBackground.default,
                                                                maxHeight: '33px',
                                                                maxWidth: '86%',
                                                                flexGrow: 1,
                                                            }}
                                                            placeholder='Dopravce'
                                                            fullWidth
                                                            onChange={(e) => {
                                                                e.preventDefault()

                                                                const newContractor = contractorList.find(
                                                                    (contractor) => contractor.id === e.target.value
                                                                )

                                                                // check if contractor is already in list
                                                                const isContractorInList = localEvent.contractors.some(
                                                                    (localContractor) =>
                                                                        localContractor.contractorId === newContractor?.id
                                                                )
                                                                if (isContractorInList)
                                                                    return dispatch(
                                                                        displayMessage({
                                                                            message: 'Dopravce je již v přiřazen k této události',
                                                                            type: 'warning',
                                                                        })
                                                                    )

                                                                if (!newContractor) return
                                                                handleChange({
                                                                    ...localEvent,
                                                                    contractors: localEvent.contractors.map((localContractor) => {
                                                                        if (
                                                                            localContractor.contractorId ===
                                                                            contractor.contractorId
                                                                        )
                                                                            return convertContractorBriefDtoToContractorOfEventDto(
                                                                                newContractor
                                                                            )
                                                                        return localContractor
                                                                    }),
                                                                })
                                                            }}
                                                            renderValue={(value) => {
                                                                return contractor.contractorName === 'Null'
                                                                    ? ''
                                                                    : contractor.contractorName
                                                            }}
                                                        >
                                                            <MenuItem>
                                                                <ListItemText
                                                                    primary='Odstranit řádek dopravce'
                                                                    style={{ color: theme.palette.error.main }}
                                                                    onClick={() => {
                                                                        handleChange({
                                                                            ...localEvent,
                                                                            contractors: localEvent.contractors.filter(
                                                                                (localContractor) =>
                                                                                    localContractor.contractorId !==
                                                                                    contractor.contractorId
                                                                            ),
                                                                        })
                                                                    }}
                                                                />
                                                            </MenuItem>
                                                            {contractorList
                                                                .filter(
                                                                    (contractor) => contractor.type === ContractorType.Carrier
                                                                )
                                                                .filter((contractor) => contractor.canBeAssigned)
                                                                .map((localContractor) => {
                                                                    const contractorForPlanning =
                                                                        convertContractorBriefDtoToContractorOfEventDto(
                                                                            localContractor
                                                                        )
                                                                    if (!contractorForPlanning.contractorId) return null

                                                                    return (
                                                                        <MenuItem
                                                                            key={contractorForPlanning.contractorId}
                                                                            value={contractorForPlanning.contractorId}
                                                                        >
                                                                            {contractorForPlanning.contractorName}
                                                                        </MenuItem>
                                                                    )
                                                                })}
                                                        </Select>
                                                        <Checkbox
                                                            disabled={contractor.contractorId === ''}
                                                            checked={contractor.isConfirmed}
                                                            style={{ padding: 0, paddingRight: 1 }}
                                                            onChange={(e) => {
                                                                if (isThereEmptySelect)
                                                                    return dispatch(
                                                                        displayMessage({
                                                                            message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                            type: 'warning',
                                                                        })
                                                                    )
                                                                handleChange({
                                                                    ...localEvent,
                                                                    contractors: localEvent.contractors.map((localContractor) =>
                                                                        contractor.contractorId === localContractor.contractorId
                                                                            ? {
                                                                                ...contractor,
                                                                                isConfirmed: e.target.checked,
                                                                            }
                                                                            : localContractor
                                                                    ),
                                                                })
                                                            }}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <CustomTextField
                                                        disabled={contractor.contractorId === ''}
                                                        fullWidth
                                                        size='small'
                                                        isSmall
                                                        placeholder='Výplata'
                                                        value={contractor.costs}
                                                        style={{
                                                            backgroundColor: contractor.gotPaid
                                                                ? theme.palette.tableRow?.background.paid
                                                                : theme.palette.customBackground.default,
                                                            maxHeight: '33px',
                                                        }}
                                                        onChange={(e) => {
                                                            if (isThereEmptySelect)
                                                                return dispatch(
                                                                    displayMessage({
                                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                        type: 'warning',
                                                                    })
                                                                )
                                                            if (isNaN(Number(e.target.value))) return
                                                            setLocalEvent({
                                                                ...localEvent,
                                                                contractors: localEvent.contractors.map((localContractor) =>
                                                                    contractor.contractorId === localContractor.contractorId
                                                                        ? {
                                                                            ...contractor,
                                                                            costs: Number(e.target.value),
                                                                        }
                                                                        : localContractor
                                                                ),
                                                            })
                                                        }}
                                                        onBlur={() => handleChange(localEvent)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleChange(localEvent)
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Checkbox
                                                        disabled={contractor.contractorId === ''}
                                                        checked={contractor.gotPaid}
                                                        style={{ padding: 0, marginTop: 3 }}
                                                        onChange={(e) => {
                                                            if (isThereEmptySelect)
                                                                return dispatch(
                                                                    displayMessage({
                                                                        message: 'Nejdříve zvolte prázndou službu / dopravce',
                                                                        type: 'warning',
                                                                    })
                                                                )
                                                            handleChange({
                                                                ...localEvent,
                                                                contractors: localEvent.contractors.map((localContractor) =>
                                                                    contractor.contractorId === localContractor.contractorId
                                                                        ? {
                                                                            ...contractor,
                                                                            gotPaid: e.target.checked,
                                                                        }
                                                                        : localContractor
                                                                ),
                                                            })
                                                        }}
                                                    />
                                                </Grid>
                                            </React.Fragment>
                                        ))
                                ) : (
                                    <Typography ml={3} variant='body1'>
                                        Není přiřazen dopravce
                                    </Typography>
                                )
                            ) : (
                                <Typography ml={3} variant='body1'>
                                    Seznam dopravců nenačten
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Stack gap={1} style={{ fontWeight: 'bold' }} mr={2} my={2} width='50%' alignItems='end'>
                    <Stack width='100%'>
                        Poznámka
                        <CustomTextField
                            size='small'
                            multiline
                            rows={8}
                            placeholder='Poznámka'
                            value={noteValue}
                            onChange={(e) => {
                                if (isThereEmptySelect)
                                    return dispatch(
                                        displayMessage({
                                            message: 'Nejdříve zvolte prázndou službu / dopravce',
                                            type: 'warning',
                                        })
                                    )
                                if (e.target.value === event.planningNote) return
                                setNoteValue(e.target.value)
                            }}
                        />
                    </Stack>
                </Stack>
            </Stack>

            {contractorsInDifferentEvent.length > 0 ? (
                <Typography component={'span'} variant='body2' color={theme.palette.warning.dark} m={2}>
                    <Stack direction='row' spacing={theme.spacing(1)} alignItems='center'>
                        <WarningAmberOutlinedIcon />
                        <Typography component={'span'} variant='body2'>
                            {contractorsInDifferentEvent.map(
                                (contractor, index) =>
                                    contractor && (
                                        <span key={index}>
                                            {contractor.name + ' (' + contractor.events.map((event) => event.eventName) + ')'}
                                            {contractorsInDifferentEvent.length > 1 &&
                                                contractorsInDifferentEvent.indexOf(contractor) !==
                                                contractorsInDifferentEvent.length - 1 ? (
                                                <span>, </span>
                                            ) : (
                                                ' '
                                            )}
                                        </span>
                                    )
                            )}
                            <span>je ve stejné časové období přiřazen/a na jinou událost.</span>
                        </Typography>
                    </Stack>
                </Typography>
            ) : (
                <></>
            )}
        </Stack>
    )
}

export default EventDetailForPlanning
